import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { usePostLoginInit } from '../hooks/usePostLoginInit';
import { InitializationContext } from '../types/initialization.types';
import { SplashScreen } from '../components/SplashScreen';
import { useAuth } from '@/features/auth/presentation/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const InitializationCtx = createContext<InitializationContext | null>(null);

interface InitializationProviderProps {
  children: ReactNode;
}

/**
 * InitializationProvider
 *
 * Wraps the application and manages post-login initialization
 * Shows splash screen during initialization phases
 * Handles errors gracefully with retry options
 */
export function InitializationProvider({ children }: InitializationProviderProps) {
  const { user: _user, isAuthenticated } = useAuth();
  const { state, initialize, retry, reset } = usePostLoginInit();
  const navigate = useNavigate();
  const location = useLocation();
  const [shouldShowSplash, setShouldShowSplash] = useState(false);

  /**
   * Determine if we should show splash screen
   * - Show during phases 1-3
   * - Hide when complete or if user is on login/register pages
   */
  useEffect(() => {
    const isAuthPage = ['/login', '/register'].includes(location.pathname);

    if (!isAuthenticated || isAuthPage) {
      setShouldShowSplash(false);
      return;
    }

    // Show splash if initializing
    const isInitializing = ['phase1', 'phase2', 'phase3'].includes(state.phase);
    setShouldShowSplash(isInitializing);

    // Auto-hide splash after completion
    if (state.isComplete) {
      setTimeout(() => {
        setShouldShowSplash(false);
      }, 500); // Small delay for smooth transition
    }
  }, [state.phase, state.isComplete, isAuthenticated, location.pathname]);

  /**
   * Reset initialization when user logs out
   */
  useEffect(() => {
    if (!isAuthenticated) {
      reset();
    }
  }, [isAuthenticated, reset]);

  /**
   * Redirect after successful initialization
   * Uses root path to let RootRedirect handle role-based routing
   */
  useEffect(() => {
    if (state.isComplete && isAuthenticated) {
      // Only redirect if we're on login/register pages
      if (['/login', '/register'].includes(location.pathname)) {
        navigate('/', { replace: true });
      }
    }
  }, [state.isComplete, isAuthenticated, location.pathname, navigate]);

  /**
   * Handle retry
   */
  const handleRetry = async () => {
    await initialize();
  };

  const contextValue: InitializationContext = {
    state,
    initialize,
    retry,
    reset,
    skipPhase: () => {}, // TODO: Implement skip phase
  };

  return (
    <InitializationCtx.Provider value={contextValue}>
      {shouldShowSplash ? (
        <SplashScreen state={state} onRetry={handleRetry} />
      ) : (
        children
      )}
    </InitializationCtx.Provider>
  );
}

/**
 * Hook to access initialization context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useInitialization(): InitializationContext {
  const context = useContext(InitializationCtx);
  if (!context) {
    throw new Error('useInitialization must be used within InitializationProvider');
  }
  return context;
}
